/**
 * ============================
 * VERIFICATION STATUS POLLER
 * ============================
 *
 * This function periodically checks the Onfido verification status
 * when it is still in the "processing" state.
 *
 * It:
 *  - Shows a "Processing..." message to the user
 *  - Polls the backend every 10 seconds
 *  - Automatically stops when status changes (approved / declined / review)
 *  - Proceeds to the next onboarding step if approved
 *  - Shows a blocked message if declined / under review
 */

export interface VerificationPollerParams {
  workflowRunId: string;
  addMessage: (msg: any) => void;
  setMessages: React.Dispatch<React.SetStateAction<any[]>>;
  setCurrentStage: (stage: string) => void;
  createMessage: (
    content: string,
    isUser?: boolean,
    meta?: Record<string, any>
  ) => any;
  showBlockedMessage: () => void;
  setDisablePrompts: (value: boolean) => void;
}

/**
 * Main polling function.
 * Keeps checking verification status until it's resolved.
 */
export async function startVerificationPolling({
  workflowRunId,
  addMessage,
  setMessages,
  setCurrentStage,
  createMessage,
  showBlockedMessage,
  setDisablePrompts,
}: VerificationPollerParams): Promise<NodeJS.Timeout> {
  // Poll every 10 seconds, stop after 2 minutes
  const pollInterval = 10_000;
  const maxDuration = 120_000;
  const startTime = Date.now();

  // Initial message
  const checkingMessage = createMessage(
    "⏳ Your ID verification is currently being processed. This usually takes a few moments — we'll keep checking automatically.",
    false,
    { actionType: "info" }
  );
  addMessage(checkingMessage);

  setCurrentStage("verification_pending");
  setDisablePrompts(true);

  const intervalId = setInterval(async () => {
    const elapsed = Date.now() - startTime;
    // Stop if it's taking too long (2 minutes)
    if (elapsed > maxDuration) {
      clearInterval(intervalId);

      const timeoutMessage = createMessage(
        "⏰ Verification is taking longer than expected. Please check again later or refresh the page.",
        false,
        { actionType: "info" }
      );
      addMessage(timeoutMessage);
      setDisablePrompts(false);
      return;
    }
    try {
      console.log("🔁 Checking Onfido verification status...");
      const res = await fetch(
        `/api/get-workflowRun-id?workflowRunId=${workflowRunId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await res.json();
      const newStatus: string | undefined = data?.data?.status;
      console.log("📦 Polled status:", newStatus);
      // Stop polling once we get a final status
      if (["approved", "declined", "review"].includes(newStatus || "")) {
        clearInterval(intervalId);
        // Remove "processing" message
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== checkingMessage.id)
        );

        if (newStatus === "approved") {
          console.log(
            "✅ Verification approved — proceeding to signature step"
          );
          // Notify backend the verification is complete
          await fetch("/api/tasks/verify-profile/complete", {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          });

          // Add success message
          const successMessage = createMessage(
            "✅ Your ID has been verified successfully! 🔒 Your information has been securely processed.",
            false,
            { actionType: "info" }
          );
          addMessage(successMessage);
          // Move to signature step
          setTimeout(() => {
            setCurrentStage("signature_pending");

            const signMsg = createMessage(
              "✍️ Please sign your documents to continue: Terms of Business, Client Care Letter and more.",
              false,
              {
                actionType: "complete",
                options: ["Sign Documents"],
              }
            );
            addMessage(signMsg);

            setTimeout(() => {
              const tipMsg = createMessage(
                "💡 Tip: Use your mouse or finger — all fields must be signed.",
                false,
                { actionType: "complete" }
              );
              addMessage(tipMsg);
            }, 500);
          }, 1000);
        } else {
          console.log("⚠️ Verification declined or under review");
          showBlockedMessage();
        }
      }
    } catch (err) {
      console.error("❌ Error while polling verification status:", err);
    }
  }, pollInterval);

  return intervalId;
}
